/**
 * アイコンアップロードステップ
 */
import { useRef, useState, ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { uploadImageFile } from "app/api";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { UserAvatar } from "common/elements";
import I18n from "features/parent/i18n";
import { CreatePlayerState } from "../types";

interface PlayerUploadIconStepProps {
  state: CreatePlayerState;
  updateState: (updates: Partial<CreatePlayerState>) => void;
  parentUid: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export const PlayerUploadIconStep = ({
  state,
  updateState,
  parentUid,
}: PlayerUploadIconStepProps) => {
  const play = usePlaySound();
  const handleDialog = useDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (file: File) => {
    setError("");

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(I18n.t("MSG_UPLOAD_ICON_TYPE_ERROR"));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(I18n.t("MSG_UPLOAD_ICON_SIZE_ERROR"));
      return;
    }

    // S3にアップロード
    const uuid = uuidv4();
    const filename = `parents/${parentUid}/assets/icons/${uuid}.png`;
    const url = `${Constants.assetHost}/${filename}`;

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      handleDialog({ type: DialogType.LOADING, value: true });
      uploadImageFile({
        filename,
        fileBlob: file,
        metadata: {
          filename: encodeURIComponent(file.name),
          size: String(file.size),
          width: String(image.width),
          height: String(image.height),
        },
        contentType: file.type,
      })
        .then(() => {
          updateState({
            icon: url,
            iconMetadata: {
              source: "uploaded" as const,
              filename: encodeURIComponent(file.name),
              content_type: file.type,
              file_size: file.size,
              width: String(image.width),
              height: String(image.height),
            },
          });
        })
        .catch(() => {
          setError(I18n.t("MSG_TOAST_ERROR"));
        })
        .finally(() => {
          handleDialog({ type: DialogType.LOADING, value: false });
          URL.revokeObjectURL(objectUrl);
        });
    };
    image.src = objectUrl;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    play();
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    play();
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    play();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  return (
    <div className="flex-col-el flex-center w-full h-full py-4">
      {/* タイトル */}
      <div className="flex-col-el flex-center mb-4">
        <p className="text-body text-textcolor-black text-[24px]">
          {I18n.t("MSG_UPLOAD_ICON_TITLE_BY_PARENT")}
        </p>
      </div>

      {/* プレビュー or アップロードエリア */}
      {state.icon ? (
        <div className="relative cursor-pointer group" onClick={handleClick}>
          <UserAvatar size={200} icon={state.icon} />
          <div className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-gray2-100 flex items-center justify-center shadow-md">
            <span className="material-symbols-outlined text-[54px] text-white">photo_camera</span>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex-col-el flex-center w-[400px] h-[250px] border-2 border-dashed rounded-[12px] cursor-pointer transition-all ${
            isDragging ? "border-blue-100 bg-blue-10" : "border-gray2-60 hover:border-blue-100"
          }`}
        >
          <span className="material-symbols-outlined !text-[64px] text-gray2-60 mb-4">
            cloud_upload
          </span>
          <p className="text-[18px] text-textcolor-black mb-2">
            {I18n.t("MSG_UPLOAD_ICON_DRAG_DROP_BY_PARENT")}
          </p>
          <p className="text-[14px] text-gray2-80">{I18n.t("MSG_UPLOAD_ICON_CLICK_BY_PARENT")}</p>
        </div>
      )}

      {/* エラーメッセージ */}
      {error && <p className="text-[14px] text-danger mt-4">{error}</p>}

      {/* ファイルサイズ・形式の注意書き */}
      <p className="text-[14px] text-gray2-80 mt-4">{I18n.t("MSG_UPLOAD_ICON_HINT_BY_PARENT")}</p>

      {/* 隠しファイル入力 */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
};
